import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";

// Color constants
const COLORS = {
  magenta: '#AA056C',
  yellowGreen: '#22C55E',
  lightPink: '#F46690',
  gray: '#64748B'
};

function Header({ sessionValid = true }) {
    // When session expires (sessionValid false) or no token, show login – don’t show logout
    const accessToken = sessionValid && sessionStorage.getItem('access_token');
    const isAdmin = sessionStorage.getItem('is_admin') === 'true';
    const navigate = useNavigate();
    const [statusMessage, setStatusMessage] = useState({ show: false, text: '', type: 'info' });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const showStatusMessage = (text, type = 'info') => {
        setStatusMessage({ show: true, text, type });
        setTimeout(() => setStatusMessage({ show: false, text: '', type: 'info' }), 5000);
    };

    const handleLogout = async () => {
        const refreshToken = sessionStorage.getItem('refresh_token');
        const accessToken = sessionStorage.getItem('access_token');
        if (!refreshToken || !accessToken) {
            sessionStorage.clear();
            window.dispatchEvent(new CustomEvent('sessionExpired'));
            navigate('/login', { replace: true });
            return;
        }
        try {
            const response = await fetch(API_ENDPOINTS.LOGOUT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ refresh: refreshToken }),
            });
            if (response.ok) {
                sessionStorage.clear();
                window.dispatchEvent(new CustomEvent('sessionExpired'));
                navigate('/login', { replace: true });
                showStatusMessage("Logout Successful", 'success');
            } else {
                // Token may already be expired/invalid; log out locally without showing noisy errors.
                if (response.status === 401 || response.status === 403) {
                    sessionStorage.clear();
                    window.dispatchEvent(new CustomEvent('sessionExpired'));
                    navigate('/login', { replace: true });
                    return;
                }
                sessionStorage.clear();
                window.dispatchEvent(new CustomEvent('sessionExpired'));
                let errorMessage = response.statusText || 'Logout failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData?.detail || errorMessage;
                    console.error("Logout failed:", response.status, errorData);
                } catch {
                    console.error("Logout failed:", response.status);
                }
                showStatusMessage(`Logout Failed: ${errorMessage}`, 'error');
                navigate('/login', { replace: true });
            }
        } catch (error) {
            console.error("Error during logout:", error);
            showStatusMessage("Error during logout: " + error.message, 'error');
        }
    };

    const handleLinkClick = () => {
        setMobileMenuOpen(false);
    };

    return (
        <>
            {/* Status Message */}
            {statusMessage.show && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
                    statusMessage.type === 'error' ? 'bg-red-100 border-l-4 border-red-500' : 
                    statusMessage.type === 'success' ? 'bg-green-100 border-l-4 border-green-500' : 
                    'bg-blue-100 border-l-4 border-blue-500'
                }`} style={{ fontFamily: 'Calibri, Verdana, sans-serif' }}>
                    <p className={`font-bold ${statusMessage.type === 'error' ? 'text-red-700' : statusMessage.type === 'success' ? 'text-green-700' : 'text-blue-700'}`} style={{ fontSize: '14px' }}>
                        {statusMessage.text}
                    </p>
                </div>
            )}

            <div className="bg-gray-100 rounded-md p-2" style={{ fontFamily: 'Calibri, Verdana, sans-serif' }}>
                {/* Desktop Navigation — flex-wrap so links stay visible on narrow viewports */}
                <nav className="hidden md:flex md:flex-wrap md:justify-center md:items-center md:gap-x-4 md:gap-y-2 text-center font-bold mb-2 px-1" style={{ fontSize: '24px', color: COLORS.magenta }}>
                    {accessToken ? (
                        <>
                            <Link to="/cash-drop" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Cash Drop</Link>
                            <Link to="/cd-dashboard" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Cash Drop Dashboard</Link>
                            <Link to="/odoo" className="inline-block py-2 px-1 transition hover:underline font-black" style={{ color: COLORS.magenta, fontSize: '18px' }}>Shop</Link>
                            {isAdmin && (
                                <Link to="/dashboard" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Admin Dashboard</Link>
                            )}
                            {isAdmin && (
                                <Link to="/cd-validation" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Cash Drop Validation</Link>
                            )}
                            {isAdmin && (
                                <Link to="/bank-drop" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Bank Drop</Link>
                            )}
                            <button type="button" onClick={handleLogout} className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Logout</button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Login</Link>
                            <Link to="/cash-drop" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Cash Drop</Link>
                            <Link to="/cd-dashboard" className="inline-block py-2 px-1 transition hover:underline" style={{ color: COLORS.magenta, fontSize: '18px' }}>Cash Drop Dashboard</Link>
                            <Link to="/odoo" className="inline-block py-2 px-1 transition hover:underline font-black" style={{ color: COLORS.magenta, fontSize: '18px' }}>Shop</Link>
                        </>
                    )}
                </nav>

                {/* Mobile Navigation — Shop always visible; rest in hamburger */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between gap-2">
                        <Link
                            to="/odoo"
                            className="font-black px-3 py-2 rounded-lg border-2 transition hover:opacity-90 shrink-0"
                            style={{ color: COLORS.magenta, borderColor: COLORS.magenta, fontSize: '16px' }}
                        >
                            Shop
                        </Link>
                        <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-md transition shrink-0"
                            style={{ color: COLORS.magenta }}
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                        <span className="font-bold truncate" style={{ fontSize: '18px', color: COLORS.magenta }}>Menu</span>
                        </div>
                    </div>

                    {/* Mobile Menu Dropdown */}
                    {mobileMenuOpen && (
                        <div className="mt-4 space-y-2 border-t pt-2" style={{ borderColor: COLORS.gray + '40' }}>
                            {accessToken ? (
                                <>
                                    <Link 
                                        to="/cash-drop" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Cash Drop
                                    </Link>
                                    <Link 
                                        to="/cd-dashboard" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Cash Drop Dashboard
                                    </Link>
                                    <Link 
                                        to="/odoo" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Shop
                                    </Link>
                                    {isAdmin && (
                                        <Link 
                                            to="/dashboard" 
                                            onClick={handleLinkClick}
                                            className="block p-3 rounded transition hover:bg-gray-200" 
                                            style={{ color: COLORS.magenta, fontSize: '16px' }}
                                        >
                                            Admin Dashboard
                                        </Link>
                                    )}
                                    {isAdmin && (
                                        <Link 
                                            to="/cd-validation" 
                                            onClick={handleLinkClick}
                                            className="block p-3 rounded transition hover:bg-gray-200" 
                                            style={{ color: COLORS.magenta, fontSize: '16px' }}
                                        >
                                            Cash Drop Validation
                                        </Link>
                                    )}
                                    {isAdmin && (
                                        <Link 
                                            to="/bank-drop" 
                                            onClick={handleLinkClick}
                                            className="block p-3 rounded transition hover:bg-gray-200" 
                                            style={{ color: COLORS.magenta, fontSize: '16px' }}
                                        >
                                            Bank Drop
                                        </Link>
                                    )}
                                    <button 
                                        onClick={() => {
                                            handleLogout();
                                            handleLinkClick();
                                        }} 
                                        className="block w-full text-left p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link 
                                        to="/login" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Login
                                    </Link>
                                    <Link 
                                        to="/cash-drop" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Cash Drop
                                    </Link>
                                    <Link 
                                        to="/cd-dashboard" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Cash Drop Dashboard
                                    </Link>
                                    <Link 
                                        to="/odoo" 
                                        onClick={handleLinkClick}
                                        className="block p-3 rounded transition hover:bg-gray-200 font-black" 
                                        style={{ color: COLORS.magenta, fontSize: '16px' }}
                                    >
                                        Shop
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Header;
